<template>
    <div class="response-display" :class="{ 'latest-response': isLatest }">
        <div class="response-card position-relative">
            <!-- Copy Icon in top right -->
            <button
                type="button"
                class="copy-icon-btn"
                v-clipboard:copy="copyText"
                v-clipboard:success="onCopySuccess"
                v-clipboard:error="onCopyError"
                title="Copy response to clipboard"
            >
                <i class="fas fa-clipboard"></i>
            </button>

            <!-- Question -->
            <div class="question-section">
                <div class="d-flex align-items-start">
                    <div class="question-label">
                        <span class="label-text">Q:</span>
                    </div>
                    <div class="question-content flex-grow-1">
                        <strong class="question-text">{{ response.question }}</strong>
                    </div>
                </div>
            </div>

            <!-- AI Response -->
            <div class="answer-section">

                <div class="d-flex align-items-start">
                    <div class="answer-label">
                        <span class="label-text">A:</span>
                    </div>
                    <div class="answer-content flex-grow-1">
                        <div class="answer-text" v-html="formattedAnswer"></div>

                        <!-- Sources -->
                        <div v-if="response.sources && response.sources.length > 0" class="sources-section mt-1">
                            <small class="text-muted">
                                <i class="fas fa-info-circle"></i>
                                Sources:
                                <span v-for="(source, index) in response.sources" :key="source">
                                    <span class="badge badge-secondary">{{ formatSource(source) }}</span>
                                    <span v-if="index < response.sources.length - 1">, </span>
                                </span>
                            </small>
                        </div>

                        <!-- Metadata (if enabled) -->
                        <div v-if="showMetadata" class="metadata-section mt-2">
                            <small class="text-muted">
                                <div class="d-flex flex-wrap gap-2">
                                    <span v-if="response.cached" class="badge badge-info">
                                        <i class="fas fa-save"></i> Cached
                                    </span>
                                    <span v-if="response.tokensUsed" class="badge badge-light">
                                        {{ response.tokensUsed }} tokens
                                    </span>
                                    <span v-if="response.model" class="badge badge-light">
                                        {{ response.model }}
                                    </span>
                                </div>
                            </small>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ResponseDisplay',

    props: {
        response: {
            type: Object,
            required: true
        },
        showMetadata: {
            type: Boolean,
            default: false
        },
        isLatest: {
            type: Boolean,
            default: false
        }
    },

    computed: {
        formattedAnswer() {
            if (!this.response.answer) return ''

            let formatted = this.response.answer

            // Convert markdown links to HTML links
            formatted = formatted.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')

            // Convert markdown bold text to HTML strong tags
            formatted = formatted.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')

            // Convert markdown italic text to HTML em tags
            formatted = formatted.replace(/\*([^*]+)\*/g, '<em>$1</em>')

            // Basic formatting: convert line breaks to HTML
            formatted = formatted
                .replace(/\n\n/g, '</p><p>')
                .replace(/\n/g, '<br>')

            // Wrap in paragraph tags if not already wrapped
            if (!formatted.startsWith('<p>')) {
                formatted = '<p>' + formatted + '</p>'
            }

            return formatted
        },

        copyText() {
            return `Q: ${this.response.question}\n\nA: ${this.response.answer}`
        }
    },

    methods: {
        formatTimestamp(timestamp) {
            if (!timestamp) return ''

            const now = new Date()
            const diff = now.getTime() - timestamp.getTime()
            const minutes = Math.floor(diff / 60000)
            const hours = Math.floor(diff / 3600000)
            const days = Math.floor(diff / 86400000)

            if (minutes < 1) {
                return 'Just now'
            } else if (minutes < 60) {
                return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`
            } else if (hours < 24) {
                return `${hours} hour${hours !== 1 ? 's' : ''} ago`
            } else if (days < 7) {
                return `${days} day${days !== 1 ? 's' : ''} ago`
            } else {
                return timestamp.toLocaleDateString()
            }
        },

        formatSource(source) {
            const sourceMap = {
                'faq': 'FAQ',
                'registration_category': 'Registration',
                'occasion': 'Event Details',
                'pricing': 'Pricing',
                'participant': 'Your Registration',
                'course_maps': 'Route Maps'
            }

            return sourceMap[source] || source.charAt(0).toUpperCase() + source.slice(1)
        },

        onCopySuccess(e) {
            this.$emit('copy', e.text)
        },

        onCopyError(e) {
            console.error('Copy failed:', e)
        }
    }
}
</script>

<style scoped>
.response-display {
}

.response-display:last-child {
    border-bottom: none;
}

.latest-response {
    border-radius: 0.375rem;
}

.response-card {
    border: 1px solid #e9ecef;
    border-radius: 0.375rem;
    padding: 1rem;
    background-color: #ffffff;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.latest-response .response-card {
    background-color: #f9f9f9;
}

.question-section {
    margin-bottom: 1rem;
}

.question-label {
    margin-right: 0.75rem;
    min-width: 20px;
}

.label-text {
    color: #6c757d;
    font-size: 0.875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.answer-section {
    /* Styling for AI response section */
}

.answer-label {
    margin-right: 0.75rem;
    min-width: 20px;
}

.answer-label .label-text {
    color: #007bff;
}

.question-text {
    color: #495057;
    font-size: 0.95rem;
}

.question-meta {
    margin-top: 0.25rem;
}

.answer-text {
    color: #212529;
    line-height: 1.6;
}

.answer-text p {
    margin-bottom: 0.75rem;
}

.answer-text p:last-child {
    margin-bottom: 0;
}

.answer-text a {
    color: #007bff;
    text-decoration: none;
    font-weight: 500;
    border-bottom: 1px solid transparent;
    transition: all 0.15s ease-in-out;
}

.answer-text a:hover {
    color: #0056b3;
    text-decoration: none;
    border-bottom-color: #0056b3;
}

.sources-section {
    /* Removed padding-top to reduce gap */
}

.metadata-section {
    border-top: 1px solid #e9ecef;
    padding-top: 0.5rem;
}

.copy-icon-btn {
    position: absolute;
    top: 0;
    right: 0;
    margin-right:10px;
    background: none;
    border: none;
    color: #6c757d;
    cursor: pointer;
    padding: 0.25rem;
    border-radius: 0.25rem;
    font-size: 0.875rem;
    transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out;
}

.badge {
    margin-right: 0.25rem;
    margin-bottom: 0.25rem;
}

.badge i {
    margin-right: 0.25rem;
}

.gap-2 > * {
    margin-right: 0.5rem;
    margin-bottom: 0.25rem;
}

@media (max-width: 576px) {
    .question-label,
    .answer-label {
        margin-right: 0.5rem;
        min-width: 20px;
    }

    .latest-response {
        border-radius: 0;
        border-left: none;
        border-right: none;
    }
}
</style>



// WEBPACK FOOTER //
// src/Assets/components/AiAgents/FaqAgent/components/ResponseDisplay.vue